import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Camera, Bot, Palette, UserCheck, TrendingUp, Gem, PenTool, PlayCircle } from 'lucide-react';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  fieldPath?: string;
  index: number;
  onClick?: () => void;
  isClickable?: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ 
  icon, 
  title, 
  description, 
  fieldPath, 
  index, 
  onClick,
  isClickable = false 
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleClick = () => {
    if (isClickable && onClick) {
      onClick();
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={isClickable ? { 
        scale: 1.02,
        y: -5,
        boxShadow: "0 25px 50px rgba(218, 165, 32, 0.15)"
      } : {}}
      className={`service-card bg-white/90 backdrop-blur-sm p-8 rounded-lg border border-amber-200/30 transition-all duration-300 group shadow-lg ${
        isClickable 
          ? 'cursor-pointer hover:border-amber-300/50 hover:shadow-xl hover:bg-amber-50/30' 
          : 'cursor-default'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(252, 248, 227, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
      }}
      data-sb-field-path={fieldPath}
      onClick={handleClick}
    >
      <motion.div 
        className="mb-6 text-amber-700"
        whileHover={isClickable ? { scale: 1.1, rotate: 5 } : {}}
        transition={{ duration: 0.3 }}
      >
        {icon}
      </motion.div>
      <h3 className={`text-xl font-semibold mb-3 text-amber-800 transition-colors ${
        isClickable ? 'group-hover:text-amber-700' : ''
      }`}>
        {title}
      </h3>
      <p className={`text-gray-700 transition-colors leading-relaxed ${
        isClickable ? 'group-hover:text-gray-600' : ''
      }`}>
        {description}
      </p>
      {isClickable && (
        <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-sm text-amber-700 font-medium">Click to explore →</span>
        </div>
      )}
    </motion.div>
  );
};

interface ServicesProps {
  disabledServices?: string[];
}

const Services: React.FC<ServicesProps> = ({ disabledServices = [] }) => {
  const navigate = useNavigate();

  const handleCopyCommandClick = () => {
    console.log('Navigating to conversion copy command page...');
    navigate('/services/conversion-copy-command');
  };

  const handleRevenueWarfareClick = () => {
    console.log('Navigating to revenue digital warfare page...');
    navigate('/services/revenue-digital-warfare');
  };

  // Helper function to check if a service should be clickable
  const isServiceClickable = (serviceName: string) => {
    return !disabledServices.includes(serviceName);
  };

  return (
    <section id="services" className="relative py-20 px-6" data-sb-field-path="sections.1">
      {/* Luxury Background with Ivory Base and Subtle Textures */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #fefbf3 0%, #faf8f0 25%, #f8f6f0 50%, #f5f3ed 75%, #f2f0ea 100%)',
      }}>
        {/* Subtle grain/texture overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        
        {/* Iridescent gradient overlays */}
        <div className="absolute inset-0 opacity-30" style={{
          background: 'linear-gradient(45deg, rgba(218, 165, 32, 0.05) 0%, rgba(192, 192, 192, 0.05) 50%, rgba(147, 112, 219, 0.05) 100%)',
        }} />
        
        {/* Circuit pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23d4af37' stroke-width='0.5'%3E%3Cpath d='M10 10h80v80H10z'/%3E%3Cpath d='M30 10v80M70 10v80M10 30h80M10 70h80'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        
        {/* Floating light elements */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={`light-${i}`}
            className="absolute rounded-full opacity-10"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 25}%`,
              width: `${80 + i * 10}px`,
              height: `${80 + i * 10}px`,
              background: 'radial-gradient(circle, rgba(218, 165, 32, 0.3) 0%, rgba(218, 165, 32, 0.1) 50%, transparent 100%)',
              filter: 'blur(20px)',
            }}
            animate={{
              y: [0, -15, 0],
              opacity: [0.1, 0.2, 0.1],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 1.5,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h2 className="text-4xl font-light text-amber-800 mb-2" data-sb-field-path="sections.1.title">
           The Manifest Suite
          </h2>
          <p className="text-gray-600" data-sb-field-path="sections.1.content">
          A private collection of digital instruments designed not just to perform, but to enchant. Reserved for those ready to be unforgettable, untouchable, and unmistakably seen. Welcome to the place where legacy is crafted, not chased.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Gold dividers between columns */}
          <div className="hidden md:block absolute left-1/3 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-amber-200/30 to-transparent" />
          <div className="hidden md:block absolute left-2/3 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-amber-200/30 to-transparent" />
          
          <ServiceCard
            icon={<Bot className="w-8 h-8" />}
            title="Custom AI Chatbot"
            description="Coming Soon"
            fieldPath="sections.2"
            index={0}
            isClickable={false}
          />
          <ServiceCard
            icon={<TrendingUp className="w-8 h-8" />}
            title="Brand Scaling & Profit Growth"
            description="Unlock your brand's full potential. We craft tailored strategies to scale your business, drive profits, and dominate your market with precision and power."
            fieldPath="sections.3"
            index={1}
            onClick={handleRevenueWarfareClick}
            isClickable={isServiceClickable('revenue-warfare')}
          />
          <ServiceCard
            icon={<PenTool className="w-8 h-8" />}
            title="Conversion Copy Command"
            description="Unlock the power of words that convert. We craft persuasive, psychology-driven copy designed to capture attention, build trust, and drive measurable action—transforming visitors into loyal customers."
            fieldPath="sections.4"
            index={2}
            onClick={handleCopyCommandClick}
            isClickable={isServiceClickable('conversion-copy')}
          />
        </div>
      </div>
    </section>
  );
};

export default Services